import { Body } from '@assembly-js/design-system';
import { arrayMove } from '@dnd-kit/sortable';
import { IconButton } from '@mui/material';
import clsx from 'clsx';
import React, { ComponentProps, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import { v4 } from 'uuid';

import {
  MoreActionsIcon,
  PlusIcon,
  ReorderIcon,
  TrashIcon,
} from 'src/components/icons';
import { CustomFieldValueData } from 'src/legacy/components/ClientDetailsPage/clientDetailsTypes';
import { BaseMenu } from 'src/legacy/components/Dropdowns';
import { SelectDropDown } from 'src/legacy/components/Select/SelectDropdown';
import {
  MultiSelectOption,
  SelectOption,
} from 'src/legacy/components/Select/types';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BaseTextField } from 'src/legacy/components/TextField';
import { useCustomFields } from 'src/services/api/customFieldsApi';
import {
  CustomFieldEntityType,
  CustomFieldOperationType,
  MultiSelectCustomField,
} from 'src/store/clients/types';
import {
  CategoricalColors,
  DividersAndCardBorders,
  GraySmall,
  MultiSelectOptionsColorsValues,
  NonHoverBorder,
  red,
} from 'src/theme/colors';
import { partition } from 'src/utils/array';
import { capitalize } from 'src/utils/StringUtils';

import { MultiSelectInput } from './MultiSelectInput';
import { OptionChip, OptionColorDisplay } from './OptionChip';
import {
  SortableMultiSelectList,
  useSortableDragHandle,
} from './SortableMultiSelectList';

const SortableOptionRow: React.FC<{
  classes: { dragIcon: string };
  children: React.ReactNode;
}> = ({ classes, children }) => {
  const { attributes, listeners } = useSortableDragHandle();
  return (
    <div className="flex w-full items-center">
      <span {...attributes} {...listeners}>
        <ReorderIcon className={classes.dragIcon} />
      </span>
      {children}
    </div>
  );
};

const isMultiSelectOption = (
  option: SelectOption,
): option is MultiSelectOption => {
  return 'color' in option;
};

const useStyles = makeStyles()(() => ({
  root: {
    width: '100%',
    minHeight: '36px',
    display: 'flex',
    alignItems: 'center',
  },
  menuInput: {
    width: '256px',
    borderBottom: `1px solid ${DividersAndCardBorders}`,
    '& .MuiInputBase-root .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  dragIcon: {
    color: NonHoverBorder,
    fontSize: 10,
  },
}));

interface MultiSelectProps {
  placeholder: string;
  initialFieldValue: string[];
  field: MultiSelectCustomField;
  onSaveProperty: (
    property: MultiSelectCustomField,
    deletedOptionKey?: string,
    operationType?: CustomFieldOperationType,
  ) => Promise<void>;
  onSaveValue: (property: CustomFieldValueData) => void;
  tagsLimit?: number;
  shouldOpenMenu?: boolean;
  showPreviewPlaceholder?: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  onTagsEmpty?: (isEmpty: boolean) => void;
  autoFocus?: boolean;
  isCellContext?: boolean;
}

const getSelectDropdownInputRenderer = ({
  classes,
  placeholder,
  autoFocus,
}: {
  classes: ReturnType<typeof useStyles>['classes'];
  placeholder: string;
  autoFocus: boolean;
}) => {
  return (params: any) => (
    <BaseTextField
      autoFocus={autoFocus}
      placeholder={placeholder}
      className={classes.menuInput}
      sizeVariant="medium"
      variant="outlined"
      {...params}
    />
  );
};

export const MultiSelect: React.FC<MultiSelectProps> = ({
  onSaveProperty,
  onSaveValue,
  initialFieldValue,
  field,
  placeholder,
  showPreviewPlaceholder = true,
  tagsLimit = 3,
  shouldOpenMenu,
  onClose,
  onTagsEmpty,
  autoFocus = true,
  isCellContext = false,
}) => {
  const { classes } = useStyles();
  const [fieldValue, setFieldValue] =
    React.useState<string[]>(initialFieldValue);
  const [inputValue, setInputValue] = React.useState('');
  const multiSelectInputRef = React.useRef<HTMLDivElement | null>(null);

  // Sync internal state when initialFieldValue changes (e.g., from websocket updates)
  React.useEffect(() => {
    setFieldValue(initialFieldValue);
  }, [initialFieldValue]);

  const clientCustomFields = useCustomFields(CustomFieldEntityType.CLIENT);
  const companyCustomFields = useCustomFields(CustomFieldEntityType.COMPANY);
  const portalCustomFields = React.useMemo(
    () => ({ ...clientCustomFields, ...companyCustomFields }),
    [clientCustomFields, companyCustomFields],
  );
  // On CRM table, when  multi select options get updated (label/tag)
  // table row cannot trigger that change since field options are not part of
  // the client row data. For that reason we've defined multiSelectOptions
  // as memoized property which listen to portalCustomFields changes to get the
  // latest updated multi select field options state.
  const multiSelectOptions = React.useMemo(() => {
    const multiSelectField = (
      portalCustomFields as Record<string, MultiSelectCustomField>
    )[field.id] as MultiSelectCustomField;

    return multiSelectField && multiSelectField.options
      ? multiSelectField.options
      : [];
  }, [portalCustomFields, field.id]);
  const selectedTags = React.useMemo(() => {
    if (!fieldValue) return [];
    const tags: MultiSelectOption[] = [];
    fieldValue.forEach((tagId: string) => {
      // we should only show tag when it is existing in multi select field options.
      const selectedTag = multiSelectOptions.find((opt) => opt.id === tagId);
      if (selectedTag) {
        tags.push(selectedTag);
      }
    });

    // notify the parent component if filtered tags
    // are empty so that it update showing field icon.
    if (onTagsEmpty) onTagsEmpty(tags.length === 0);

    return tags;
  }, [multiSelectOptions, fieldValue, onTagsEmpty]);
  const [multiSelectAnchorEl, setMultiSelectAnchorEl] =
    React.useState<HTMLElement | null>(null);

  const [editingOption, setEditingOption] =
    React.useState<MultiSelectOption | null>(null);
  const [editOptionMenuAnchorEl, setEditOptionMenuAnchorEl] =
    React.useState<HTMLButtonElement | null>(null);
  const [nextOptionColorIndex, setNextOptionColorIndex] = React.useState(0);
  /**
   * Here we're listening to shouldOpenMenu changes.
   * When it is truthy it should open the multi select menu
   * by setting the multi select input component ref as
   * menu anchor.
   */
  React.useEffect(() => {
    if (shouldOpenMenu && multiSelectInputRef.current) {
      setMultiSelectAnchorEl(multiSelectInputRef.current || null);
    }
  }, [shouldOpenMenu]);

  /**
   * This function will update the next tag color index.
   * Tags colors are  roundly assigned so we keep creating
   * new tag with new colors.
   */
  const updateNextOptionColor = () => {
    if (nextOptionColorIndex < MultiSelectOptionsColorsValues.length - 1) {
      setNextOptionColorIndex(nextOptionColorIndex + 1);
    } else {
      setNextOptionColorIndex(0);
    }
  };
  /**
   * This function handle select dropdown change
   * event. When create-option item is selected
   * we need to update the property (custom field)
   * options with the added option. Otherwise it
   * @param _ev
   * @param selectedOptions
   * @returns
   */
  const handleChange: ComponentProps<
    typeof SelectDropDown
  >['onChange'] = async (_ev, selectedOptions) => {
    setInputValue('');
    const selectedOption = (selectedOptions as MultiSelectOption[])[
      (selectedOptions as MultiSelectOption[]).length - 1
    ];
    if (selectedOption && selectedOption?.id === 'add-new-option-item') {
      const newOptionId = v4();
      const newOption = {
        id: `option-${newOptionId}`,
        label: selectedOption.label,
        color: 'none',
        order: 0,
      };
      const updatedMultiSelectProperty = {
        ...field,
        options: [...(multiSelectOptions || []), ...[newOption]],
      };

      // when new tag is created we should set it in field value
      setFieldValue((fieldValue || []).concat([newOption.id]));
      updateNextOptionColor();
      setEditOptionMenuAnchorEl(null);

      // Clear inputValue only when a tag is created
      setInputValue('');

      try {
        await onSaveProperty(
          updatedMultiSelectProperty as MultiSelectCustomField,
          undefined,
          'createOption',
        );
        onSaveValue({
          fieldId: field.id,
          value: (fieldValue || []).concat([newOption.id]),
        });
      } catch (error) {
        console.error('Failed to save property:', error);
      }
    } else {
      const selectedOptionsIds = (selectedOptions as MultiSelectOption[]).map(
        (option) => option.id,
      );
      onSaveValue({
        fieldId: field.id,
        value: selectedOptionsIds,
      });
      setFieldValue(selectedOptionsIds);
    }
  };

  const handleOpenEditOptionMenu = (
    ev: React.MouseEvent<HTMLButtonElement>,
    option: MultiSelectOption,
  ) => {
    // when option menu button is clicked
    // we need to stop event propagation
    // so that change event is not triggered.
    ev.stopPropagation();
    setEditOptionMenuAnchorEl(ev.currentTarget);
    setEditingOption(option);
  };
  /**
   * When delete icon button is clicked from the edit menu,
   * this function is triggered to perform the delete action.
   * It removes the deleting option from options array
   * and calls saveProperty method to update the custom
   * field entity.
   */
  const handleRemoveOption = async () => {
    const [[deletedOption], filteredMultiSelectPropertyOptions] = partition(
      multiSelectOptions,
      (option) => option.id === editingOption?.id,
    );
    // when a tag is removed from menu list, it should be
    // filtered from input values.
    const updatedFieldValue = selectedTags
      .filter((option) => option.id !== editingOption?.id)
      .map((option) => option.id);

    // close edit option menu after remove is performed
    setEditOptionMenuAnchorEl(null);

    try {
      await onSaveProperty(
        {
          ...field,
          options: filteredMultiSelectPropertyOptions,
        },
        deletedOption.id,
        'deleteOption',
      );
      onSaveValue({
        fieldId: field.id,
        value: updatedFieldValue,
      });
    } catch (error) {
      console.error('Failed to save property:', error);
    }
  };

  /**
   * This method is triggered when option color is
   * clicked. It takes the color value and update the
   * multi select property with updated option object.
   * @param colorVal: color option item value
   */
  const handleEditOptionColor = async (colorVal: string) => {
    const updatedMultiSelectOptions = multiSelectOptions.map((option) => {
      if (option.id === editingOption?.id) {
        return {
          ...editingOption,
          color: colorVal,
        };
      }
      return option;
    });

    await onSaveProperty(
      {
        ...field,
        options: updatedMultiSelectOptions,
      } as MultiSelectCustomField,
      undefined,
      'colorChange',
    );

    // close edit option menu after tag color is updated
    setEditOptionMenuAnchorEl(null);
  };

  /**
   * This function handle updating multi select option (tag)
   * label change. New label value is submitted when enter key
   * is pressed.
   * @param
   */
  const handleMultiSelectOptionLabelChange = async (
    ev: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    const optionLabelValue = ev.currentTarget.value;
    // when enter key is pressed updating option label
    // and call save property method to save it in custom
    // fields settings.
    if (ev.keyCode === 13) {
      const updatedMultiSelectOptions = multiSelectOptions.map((option) => {
        if (option.id === editingOption?.id) {
          return {
            ...editingOption,
            label: optionLabelValue,
          };
        }
        return option;
      });

      await onSaveProperty(
        {
          ...field,
          options: updatedMultiSelectOptions,
        },
        undefined,
        'updateOption',
      );
      // close edit option menu after tag label is updated
      setEditOptionMenuAnchorEl(null);
    }
  };

  /**
   * This method handle swapping multiselect
   * options and save them inside the property
   * entity.
   * @param sortData
   * @returns
   */
  const handleSortOptions = async (oldIndex: number, newIndex: number) => {
    if (newIndex === oldIndex) return;
    const sortedMultiSelectOptions = arrayMove(
      multiSelectOptions,
      oldIndex,
      newIndex,
    );

    await onSaveProperty(
      {
        ...field,
        options: sortedMultiSelectOptions,
      },
      undefined,
      'reorder',
    );
  };
  // To prevent click on the control resulting in row click, event bubbling needs to be stopped.
  const handleMultiSelectInput = (
    ev: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    ev.stopPropagation();
    setMultiSelectAnchorEl(ev.currentTarget);
  };

  const getMultiSelectOptionColorsActions = () => {
    return CategoricalColors.map((color) => ({
      key: color,
      avatar: (
        <OptionColorDisplay
          className={clsx('h-[18px] w-[18px] rounded', {
            [`relative border border-solid border-gray-500 after:content-[''] after:absolute after:top-1/2 after:left-1/2
            after:w-[140%] after:h-[1px] after:bg-gray-500
            after:-translate-x-1/2 after:-translate-y-1/2
            after:rotate-45`]: color === 'none',
          })}
          color={color}
        />
      ),
      name: capitalize(color),
      onClick: () => handleEditOptionColor(color),
    }));
  };

  const removeOptionFromSelection = useCallback(
    (tagId: string) => {
      const updatedFieldValue = selectedTags
        .filter((option) => option.id !== tagId)
        .map((option) => option.id);
      setFieldValue(updatedFieldValue);
      onSaveValue({
        fieldId: field.id,
        value: updatedFieldValue,
      });
    },
    [selectedTags, field.id, onSaveValue],
  );
  return (
    <>
      <div
        ref={multiSelectInputRef}
        className="flex w-full items-center min-h-8 pr-4"
        onClickCapture={handleMultiSelectInput}
      >
        {selectedTags.length > 0 && (
          <MultiSelectInput
            isCellContext={isCellContext}
            selectedTags={selectedTags}
            tagsLimit={tagsLimit}
          />
        )}

        {showPreviewPlaceholder && selectedTags.length === 0 && (
          <Body size="sm" className="text-light-gray cursor-pointer">
            {placeholder}
          </Body>
        )}
      </div>
      {Boolean(multiSelectAnchorEl) && (
        <SelectDropDown<MultiSelectOption, true, true>
          creatable
          listHeaderTitle="Select a tag or create one"
          isMultiple
          onClose={() => {
            setMultiSelectAnchorEl(null);
            if (onClose) onClose();
          }}
          autoCompleteClasses={{
            option: 'flex',
          }}
          open={Boolean(multiSelectAnchorEl)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          forcePopupIcon={false}
          placeholder={placeholder}
          renderTags={(opts) => (
            <div className="flex items-center flex-wrap py-1 gap-1">
              {opts.map((option) => {
                if (!('color' in option)) return null;
                return (
                  <OptionChip
                    className="[&>p]:truncate max-w-[245px] [&>p]:max-w-full"
                    key={option.id}
                    label={option.label}
                    color={option.color}
                    onClose={() => removeOptionFromSelection(option.id)}
                  />
                );
              })}
            </div>
          )}
          // To be able to render tags inside the
          // menu input we should use text field
          // and not the default base menu input
          inputRenderer={getSelectDropdownInputRenderer({
            autoFocus,
            placeholder,
            classes,
          })}
          disableClearable
          anchorEl={multiSelectAnchorEl}
          anchorOrigin={{
            horizontal: 'left',
            vertical: 'top',
          }}
          onChange={handleChange}
          inputValue={inputValue}
          onInputChange={(_ev, newInputValue, reason) => {
            if (reason !== 'reset') {
              setInputValue(newInputValue);
            }
          }}
          value={selectedTags}
          options={multiSelectOptions}
          // multi select options labels need to be wrapped
          // inside chips (tags), so we need to pass a custom
          // option renderer function as param.
          // eslint-disable-next-line react/no-unstable-nested-components
          customOptionRenderer={(props, option: SelectOption) =>
            option.id === 'add-new-option-item' ? (
              <li
                {...props}
                key="add-new-option-item"
                data-option-id={option.id}
              >
                <div className="flex items-center">
                  <PlusIcon style={{ fontSize: 9 }} />
                  <BaseTypography style={{ marginLeft: 8 }}>
                    Create
                  </BaseTypography>

                  <OptionChip
                    label={option.label}
                    color="none"
                    className="ml-1 max-w-[165px] [&>p]:truncate [&>p]:max-w-full"
                  />
                </div>
              </li>
            ) : (
              <li {...props} key={option.id} data-option-id={option.id}>
                <SortableOptionRow classes={{ dragIcon: classes.dragIcon }}>
                  <div className="w-full pl-4 flex justify-between items-center">
                    {isMultiSelectOption(option) && (
                      <OptionChip
                        label={option.label}
                        color={option.color}
                        className="max-w-[165px] [&>p]:truncate [&>p]:max-w-full"
                      />
                    )}
                    <IconButton
                      aria-label="edit option actions"
                      aria-controls="multi-select-option-edit-menu"
                      aria-haspopup="true"
                      onClickCapture={(
                        ev: React.MouseEvent<HTMLButtonElement>,
                      ) =>
                        handleOpenEditOptionMenu(
                          ev,
                          option as MultiSelectOption,
                        )
                      }
                      color="secondary"
                      size="small"
                    >
                      <MoreActionsIcon
                        htmlColor={GraySmall}
                        style={{ fontSize: 20 }}
                      />
                    </IconButton>
                  </div>
                </SortableOptionRow>
              </li>
            )
          }
          ListboxProps={{
            onSortTagsOptions: handleSortOptions,
          }}
          ListboxComponent={SortableMultiSelectList}
        />
      )}
      {/** This menu is the sub-menu showing the tags colors list */}
      {Boolean(editOptionMenuAnchorEl) && (
        <BaseMenu
          showTitleDivider={false}
          menuTitle={
            <BaseTypography fontType="10Medium">Colors</BaseTypography>
          }
          menuInputProps={{
            onKeyDown: handleMultiSelectOptionLabelChange,
            defaultValue: editingOption?.label,
            endAdornment: (
              <IconButton onClick={handleRemoveOption}>
                <TrashIcon style={{ color: red, fontSize: 12 }} />
              </IconButton>
            ),
          }}
          menuProps={{
            anchorEl: editOptionMenuAnchorEl,
            open: Boolean(editOptionMenuAnchorEl),
            onClose: () => setEditOptionMenuAnchorEl(null),
            anchorOrigin: {
              vertical: 20,
              horizontal: 'center',
            },
          }}
          actions={getMultiSelectOptionColorsActions()}
        />
      )}
    </>
  );
};
